import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { API_FACEBOOK_PAGES, API_RESOLVE_FAILING_SN } from '../utils/constants';
import { authSelector } from '../store/slices/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const axios = require('axios');

const NewFacebookPageConnection = ({
  open,
  onOpenChange,
  currentClientId,
  currentClient,
  connectSnCallback
}) => {
  const { isAuthenticated, userInfo } = useSelector(authSelector);
  const [optionsData, setOptionsData] = useState([]);
  const [facebookPage, setFacebookPage] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  const [isFailing, setIsFailing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleResolved = async (snName) => {
    let config = {
      method: 'post',
      url: API_RESOLVE_FAILING_SN,
      data: { clientId: currentClientId, snName },
      headers: { 'Content-Type': 'application/json' }
    };

    if (isFailing) {
      setIsLoading(true);
      await axios(config)
        .then(async (response) => {
          if (response.status === 200) {
            setIsFailing(false);
            setIsLoading(false);
            await someFn();
          }
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentClient?.clientSocialNetworks?.fbFailing !== undefined) {
      setIsFailing(currentClient?.clientSocialNetworks?.fbFailing);
    } else {
      setIsFailing(false);
    }
  }, [currentClient, currentClientId]);

  const getPages = async () => {
    setIsLoading(true);
    await axios
      .get(API_FACEBOOK_PAGES, {
        headers: {
          Authorization: `bearer ${userInfo.accessToken}`
        }
      })
      .then((response) => {
        const pagesData = response.data.pages;
        setOptionsData(pagesData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('error ' + error);
        throw new Error(error);
      });
  };

  useEffect(() => {
    if (optionsData?.length === 0 && !isLoading) {
      getPages();
    }
    // eslint-disable-next-line
  }, []);

  const handleSelectChanges = (option) => {
    if (option) {
      const options = { id: option.value, entity_name: option.label };
      setFacebookPage(options);
      setClientId(currentClientId);
    }
  };

  const facebookPageConnectionHandler = () => {
    setSavingClient(true);
    if (facebookPage == null) {
      alert('Please select a page.');
      setSavingClient(false);
      return;
    }
    const bodyPut = {
      clientId: clientId,
      facebookPageId: facebookPage.id,
      facebookPageName: facebookPage.entity_name
    };

    const url = API_FACEBOOK_PAGES;

    axios
      .put(url, bodyPut, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${userInfo.accessToken}`
        }
      })
      .then(async function (response) {
        if (response.status === 200) {
          await someFn();
          setTimeout(() => {
            setSavingClient(false);
            setFacebookPage(null);
            setOptionsData(null);
            onOpenChange(false); // Close dialog
          }, 6000);
        }
      })
      .catch(function (response) {
        setSavingClient(false);
        setFacebookPage(null);
      });
  };

  const someFn = async () => {
    await connectSnCallback();
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused
        ? 'hsl(var(--primary))'
        : 'hsl(var(--border))',
      boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      zIndex: 50
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary))'
        : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
      cursor: 'pointer'
    })
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Connect Facebook Page
          </DialogTitle>
          <DialogDescription>
            Select the Facebook page to connect with{' '}
            {currentClient?.clientName || 'this client'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Facebook Page Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Facebook Page</Label>
              <span className="text-sm text-muted-foreground">
                {optionsData?.length || 0} pages available
              </span>
            </div>
            <Select
              options={optionsData?.map((option) => ({
                value: option.id,
                label: option.entity_name
              }))}
              onChange={handleSelectChanges}
              isSearchable
              isClearable
              placeholder="Search for a Facebook page..."
              isLoading={isLoading}
              styles={customSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <p className="text-xs text-muted-foreground">
              Select the Facebook page you want to connect to this client
            </p>
          </div>

          {/* Failing Status Alert */}
          {isAuthenticated &&
            userInfo.role === 'superAdmin' &&
            isFailing &&
            currentClient?.clientSocialNetworks?.facebookPage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      This Facebook connection is failing
                    </p>
                    <p className="text-xs">
                      Currently connected to:{' '}
                      {currentClient.clientSocialNetworks.facebookPage}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="resolve-fb-checkbox"
                      className="text-xs cursor-pointer">
                      Mark as resolved
                    </label>
                    <input
                      id="resolve-fb-checkbox"
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      onChange={() => handleResolved('facebook')}
                      checked={!isFailing}
                      disabled={isLoading}
                    />
                    {isLoading && (
                      <Loader2 className="h-4 w-4 animate-spin ml-1" />
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

          {/* Success indicator when page is selected */}
          {facebookPage && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Selected: <strong>{facebookPage.entity_name}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={savingClient}>
            Cancel
          </Button>
          <Button
            onClick={facebookPageConnectionHandler}
            disabled={savingClient || !facebookPage}
            className="min-w-[120px]">
            {savingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Page'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewFacebookPageConnection;
